import React, { useState, useEffect } from 'react';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { API } from '../config';

// Confetti celebration function
const triggerCelebration = () => {
  // Fire confetti from the center
  const count = 200;
  const defaults = {
    origin: { y: 0.7 },
    zIndex: 9999
  };

  function fire(particleRatio, opts) {
    confetti({
      ...defaults,
      ...opts,
      particleCount: Math.floor(count * particleRatio)
    });
  }

  // Multiple bursts for maximum impact
  fire(0.25, {
    spread: 26,
    startVelocity: 55,
    colors: ['#10B981', '#34D399', '#6EE7B7'] // Emerald colors
  });
  fire(0.2, {
    spread: 60,
    colors: ['#FFD700', '#FFA500', '#FF6347'] // Gold, orange, tomato
  });
  fire(0.35, {
    spread: 100,
    decay: 0.91,
    scalar: 0.8,
    colors: ['#10B981', '#FFD700', '#FF6B6B', '#4ECDC4']
  });
  fire(0.1, {
    spread: 120,
    startVelocity: 25,
    decay: 0.92,
    scalar: 1.2,
    colors: ['#10B981', '#34D399']
  });
  fire(0.1, {
    spread: 120,
    startVelocity: 45,
    colors: ['#FFD700', '#FFA500']
  });
};

const GentleStepsCard = ({ journey, onLog, streak }) => {
  const [todayLogged, setTodayLogged] = useState(false);
  const [todayCompleted, setTodayCompleted] = useState(false);
  const [logging, setLogging] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);

  useEffect(() => {
    // Check if today is already logged
    const today = new Date().toISOString().split('T')[0];
    const todayLog = journey?.daily_logs?.find(log => log.date === today);
    if (todayLog) {
      setTodayLogged(true);
      setTodayCompleted(todayLog.completed);
    }
  }, [journey]);

  const handleLog = async (completed) => {
    setLogging(true);
    try {
      const res = await axios.post(`${API}/journey/log`, { completed });
      setTodayLogged(true);
      setTodayCompleted(completed);
      
      // 🎉 Trigger confetti celebration when user completes their Tiny Win!
      if (completed) {
        setShowCelebration(true);
        triggerCelebration();
        
        // Hide celebration message after 3 seconds
        setTimeout(() => setShowCelebration(false), 3000);
      }
      
      if (onLog) onLog(res.data);
    } catch (err) {
      alert('Failed to log progress. Please try again.');
    }
    setLogging(false);
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <span className="mr-2">🌱</span> Today's Tiny Win
          </h3>
          <p className="text-sm text-gray-500 mt-1">{journey.anchor_habit}</p>
        </div>
        {streak > 0 && (
          <div className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm font-medium">
            🔥 {streak} day streak
          </div>
        )}
      </div>
      
      <div className="bg-emerald-50 rounded-xl p-4 mb-4">
        <p className="text-emerald-800 font-medium text-center text-lg">
          "{journey.tiny_win}"
        </p>
      </div>

      {todayLogged ? (
        <div className={`text-center p-4 rounded-xl ${todayCompleted ? 'bg-green-50' : 'bg-gray-50'}`}>
          {todayCompleted ? (
            <>
              <span className="text-4xl">{showCelebration ? '🎉' : '✅'}</span>
              <p className="text-green-700 font-medium mt-2">
                {showCelebration ? "Amazing! You're building momentum!" : "Done for today! Great job!"}
              </p>
              {showCelebration && (
                <p className="text-green-600 text-sm mt-1 animate-pulse">
                  🔥 Keep the streak alive!
                </p>
              )}
            </>
          ) : (
            <>
              <span className="text-4xl">😴</span>
              <p className="text-gray-600 font-medium mt-2">Rest day logged. Tomorrow's a new day!</p>
            </>
          )}
          <button
            onClick={() => setTodayLogged(false)}
            className="text-sm text-gray-500 hover:text-gray-700 mt-2"
          >
            Change response
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-center text-gray-600 mb-4">Did you do your Tiny Win today?</p>
          <div className="flex space-x-3">
            <button
              onClick={() => handleLog(true)}
              disabled={logging}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-4 rounded-xl transition disabled:opacity-50"
            >
              ✓ Yes, I did it!
            </button>
            <button
              onClick={() => handleLog(false)}
              disabled={logging}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-4 rounded-xl transition disabled:opacity-50"
            >
              Not today
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GentleStepsCard;
